import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWorldState } from '../components/state/WorldStateContext.jsx';
import { AIRCRAFT_DATABASE, getAircraftImage, getAircraftImageSmart, getCollectibleImagePath } from '../data/aircraft.js';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Plane,
  ChevronLeft,
  Clock,
  Star,
  Trophy,
  Crown,
  Zap,
  Users,
  Gauge,
  Gem,
  Activity,
} from 'lucide-react';

// Rarity configurations with visual theming
const RARITY_CONFIG = {
  mythic: {
    color: '#ff0066',
    glow: 'rgba(255, 0, 102, 0.5)',
    gradient: 'linear-gradient(135deg, #2d0a1a, #4a102e)',
    icon: Crown,
    label: 'MYTHIC',
    border: '#ff0066',
  },
  ascendent: {
    color: '#ff8800',
    glow: 'rgba(255, 136, 0, 0.4)',
    gradient: 'linear-gradient(135deg, #2d1a0a, #4a2a10)',
    icon: Star,
    label: 'ASCENDENT',
    border: '#ff8800',
  },
  legendary: {
    color: '#f59e0b',
    glow: 'rgba(245, 158, 11, 0.4)',
    gradient: 'linear-gradient(135deg, #2d1f0a, #4a3310)',
    icon: Trophy,
    label: 'LEGENDARY',
    border: '#f59e0b',
  },
  anomalous: {
    color: '#a855f7',
    glow: 'rgba(168, 85, 247, 0.4)',
    gradient: 'linear-gradient(135deg, #1a0a2e, #2d1b4e)',
    icon: Zap,
    label: 'ANOMALOUS',
    border: '#a855f7',
  },
  relic: {
    color: '#cd7f32',
    glow: 'rgba(205, 127, 50, 0.4)',
    gradient: 'linear-gradient(135deg, #2d1a0a, #4a2a10)',
    icon: Clock,
    label: 'RELIC',
    border: '#cd7f32',
  },
  rare: {
    color: '#3b82f6',
    glow: 'rgba(59, 130, 246, 0.3)',
    gradient: 'linear-gradient(135deg, #0a1a2d, #102a4a)',
    icon: Gem,
    label: 'RARE',
    border: '#3b82f6',
  },
  standard: {
    color: '#737373',
    glow: 'rgba(115, 115, 115, 0.2)',
    gradient: 'linear-gradient(135deg, #0c0c0c, #1a1a1a)',
    icon: Plane,
    label: 'STANDARD',
    border: '#333',
  },
};

// Special exhibit descriptions for museum display
const EXHIBIT_LORE = {
  'Concorde': "Supersonic elegance. London to New York in 3.5 hours. The future that was.",
  'SPIRIT_STL': "May 20-21, 1927. Lindbergh crossed the Atlantic alone. Aviation would never be the same.",
  'ENOLA_GAY': "August 6, 1945. History's heaviest payload. A moment that divided time itself.",
  'DC10_SHIP1': "The first DC-10 ever built. Museum quality restoration of an American classic.",
  '747_CITYEVERETT': "The original Jumbo Jet. Pan Am's flagship that made the world smaller.",
  '707_PIONEER': "The jet age begins. October 26, 1958. Pan Am Flight 114 changes everything.",
  'TRISTAR': "Lockheed's masterpiece. Over-engineered, under-appreciated. A pilot's dream.",
  'CARAVELLE': "French elegance meets rear-mounted engines. Europe's first jet airliner success.",
  'COMET4': "Redemption. After tragedy, the Comet 4 proved jet travel was the future.",
  'A300B1': "The first Airbus. 1974. A European consortium against American giants.",
  'MD80_MADDOG': "The Mad Dog. The pencil jet. FedEx's midnight warrior. Legend.",
  '727_TRIJET': "Boeing's trijet conquered short runways across America and beyond.",
  '707-120': "Where it all began. Pan Am's first jet, the 707 shrank the world overnight.",
  '727-200': "The stretched trijet that became America's workhorse for three decades.",
  'Comet-4C': "After the tragedies, de Havilland rebuilt. The Comet 4C was vindication.",
  'DC-10-30': "Douglas's wide-body warrior. Controversial but capable across all oceans.",
  'L-1011-500': "The TriStar's long-range swan song. Beautifully over-engineered to the end.",
  'Tu-144': "The Soviet Concorde. First to fly supersonic, first to carry passengers, first to crash.",
  'Boeing-307': "The Stratoliner. First pressurized cabin. Flying above the weather since 1940.",
  'Lockheed-Constellation': "The Connie. Those triple tails. Howard Hughes' masterpiece.",
  'The Code': "Origin unknown. Thinks therefore it flies. Transmission intercept: eJwryUhVyCtNzklNLFJIzk9JLVYoLU5NUSjJV0hKVTCAAgD7JwvV",
};

export default function Museum() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { worldState } = useWorldState();
  const [filter, setFilter] = useState('all');

  const aircraft = worldState?.aircraft || [];

  // Group aircraft by rarity for filtering
  const groupedAircraft = useMemo(() => {
    const groups = {
      mythic: [],
      ascendent: [],
      legendary: [],
      anomalous: [],
      relic: [],
      rare: [],
      standard: [],
    };

    aircraft.forEach(ac => {
      const rarity = ac.rarity || 'standard';
      if (groups[rarity]) {
        groups[rarity].push(ac);
      } else {
        groups.standard.push(ac);
      }
    });

    return groups;
  }, [aircraft]);

  // Get filtered aircraft based on current filter
  const displayedAircraft = useMemo(() => {
    if (filter === 'all') return aircraft;
    return groupedAircraft[filter] || [];
  }, [aircraft, groupedAircraft, filter]);

  // Calculate collection stats
  const stats = useMemo(() => {
    const totalFlightHours = aircraft.reduce((sum, ac) => sum + (ac.totalFlightHours || 0), 0);
    const totalCycles = aircraft.reduce((sum, ac) => sum + (ac.totalCycles || 0), 0);
    const rareCount = aircraft.filter(ac => ac.rarity && ac.rarity !== 'standard').length;
    const uniqueTypes = new Set(aircraft.map(ac => ac.model || ac.aircraftType)).size;
    return { totalFlightHours, totalCycles, rareCount, uniqueTypes };
  }, [aircraft]);

  const getAircraftSpecs = (ac) => {
    return AIRCRAFT_DATABASE[ac.model] || AIRCRAFT_DATABASE[ac.aircraftType] || ac;
  };

  const getRarityConfig = (rarity) => {
    return RARITY_CONFIG[rarity] || RARITY_CONFIG.standard;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return t('museum.unknown');
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return t('museum.unknown');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#050505' }}>
      {/* Atmospheric Background Gradient */}
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at 50% 0%, rgba(96, 255, 208, 0.03) 0%, transparent 60%)',
        }}
      />

      {/* Subtle stars effect */}
      <div
        className="fixed inset-0 pointer-events-none opacity-30"
        style={{
          backgroundImage: `radial-gradient(1px 1px at 20px 30px, #60ffd0, transparent),
                           radial-gradient(1px 1px at 40px 70px, #3b82f6, transparent),
                           radial-gradient(1px 1px at 50px 160px, #a855f7, transparent),
                           radial-gradient(1px 1px at 90px 40px, #f59e0b, transparent),
                           radial-gradient(1px 1px at 130px 80px, #60ffd0, transparent)`,
          backgroundSize: '200px 200px',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate("/opscenter")}
              style={{ color: "#737373" }}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {t('museum.back')}
            </Button>
            <div>
              <h1
                className="text-4xl font-bold tracking-wider"
                style={{
                  background: 'linear-gradient(90deg, #60ffd0, #3b82f6, #a855f7)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                {t('museum.title')}
              </h1>
              <p className="text-sm mt-1" style={{ color: '#525252' }}>
                {t('museum.subtitle')}
              </p>
            </div>
          </div>

          {/* Collection Stats */}
          <div className="flex items-center gap-6">
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: '#60ffd0' }}>{aircraft.length}</div>
              <div className="text-xs" style={{ color: '#525252' }}>{t('museum.aircraft')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: '#a855f7' }}>{stats.uniqueTypes}</div>
              <div className="text-xs" style={{ color: '#525252' }}>{t('museum.uniqueTypes')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: '#f59e0b' }}>{stats.rareCount}</div>
              <div className="text-xs" style={{ color: '#525252' }}>{t('museum.rareFinds')}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: '#3b82f6' }}>
                {Math.floor(stats.totalFlightHours).toLocaleString()}
              </div>
              <div className="text-xs" style={{ color: '#525252' }}>{t('museum.flightHours')}</div>
            </div>
          </div>
        </div>

        {/* Navigation buttons */}
        <div className="flex gap-3 mb-6">
          <Button
            onClick={() => navigate("/fleet")}
            style={{ backgroundColor: '#1a1a1a', color: '#a3a3a3', border: '1px solid #333' }}
          >
            <Plane className="w-4 h-4 mr-2" /> {t('museum.fleetManagement')}
          </Button>
          <Button
            onClick={() => navigate("/WorldMap")}
            style={{ backgroundColor: '#1a1a1a', color: '#a3a3a3', border: '1px solid #333' }}
          >
            <Activity className="w-4 h-4 mr-2" /> {t('museum.worldMap')}
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { key: 'all', labelKey: 'allAircraft', count: aircraft.length },
            { key: 'mythic', labelKey: 'mythic', count: groupedAircraft.mythic.length },
            { key: 'ascendent', labelKey: 'ascendent', count: groupedAircraft.ascendent.length },
            { key: 'legendary', labelKey: 'legendary', count: groupedAircraft.legendary.length },
            { key: 'anomalous', labelKey: 'anomalous', count: groupedAircraft.anomalous.length },
            { key: 'relic', labelKey: 'relic', count: groupedAircraft.relic.length },
            { key: 'rare', labelKey: 'rare', count: groupedAircraft.rare.length },
            { key: 'standard', labelKey: 'standard', count: groupedAircraft.standard.length },
          ].map(tab => (
            <Button
              key={tab.key}
              size="sm"
              onClick={() => setFilter(tab.key)}
              style={{
                backgroundColor: filter === tab.key
                  ? (RARITY_CONFIG[tab.key]?.color || '#60ffd0')
                  : '#1a1a1a',
                color: filter === tab.key ? '#000' : '#737373',
                border: `1px solid ${filter === tab.key ? 'transparent' : '#333'}`,
                transition: 'all 0.2s',
              }}
            >
              {t(`museum.${tab.labelKey}`)} ({tab.count})
            </Button>
          ))}
        </div>

        {/* Empty State */}
        {displayedAircraft.length === 0 && (
          <div className="text-center py-20">
            <Gem className="w-16 h-16 mx-auto mb-4" style={{ color: '#333' }} />
            <p className="text-lg mb-2" style={{ color: '#525252' }}>
              {filter === 'all'
                ? t('museum.emptyAll')
                : t('museum.emptyFilter', { filter: t(`museum.${filter}`) })}
            </p>
            <p className="text-sm mb-6" style={{ color: '#3a3a3a' }}>
              {t('museum.emptyHint')}
            </p>
            <Button
              onClick={() => navigate("/AircraftMarket")}
              style={{ backgroundColor: '#f59e0b', color: '#000' }}
            >
              {t('museum.browseMarket')}
            </Button>
          </div>
        )}

        {/* Aircraft Grid - Museum Style Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedAircraft.map((ac) => {
            const specs = getAircraftSpecs(ac);
            const rarityConfig = getRarityConfig(ac.rarity);
            const RarityIcon = rarityConfig.icon;
            const lore = EXHIBIT_LORE[ac.model] || specs.description || t('museum.defaultLore');

            return (
              <Card
                key={ac.id}
                className="overflow-hidden"
                style={{
                  background: rarityConfig.gradient,
                  border: `2px solid ${rarityConfig.border}`,
                  boxShadow: `0 0 20px ${rarityConfig.glow}`,
                }}
              >
                {/* Large Image Section */}
                <div
                  className="h-48 relative overflow-hidden"
                  style={{ backgroundColor: '#0a0a0a' }}
                >
                  <img
                    src={getAircraftImageSmart(ac)}
                    alt={ac.model || ac.aircraftType || 'Aircraft'}
                    className="w-full h-full object-contain p-4"
                    style={{
                      filter: `drop-shadow(0 0 15px ${rarityConfig.glow})`,
                    }}
                    onError={(e) => {
                      e.target.style.opacity = '0.3';
                    }}
                  />

                  {/* Rarity Badge */}
                  {ac.rarity && ac.rarity !== 'standard' && (
                    <div
                      className="absolute top-3 right-3 px-3 py-1 rounded-full flex items-center gap-1"
                      style={{
                        backgroundColor: `${rarityConfig.color}20`,
                        border: `1px solid ${rarityConfig.color}`,
                        backdropFilter: 'blur(4px)',
                      }}
                    >
                      <RarityIcon className="w-3 h-3" style={{ color: rarityConfig.color }} />
                      <span className="text-xs font-bold" style={{ color: rarityConfig.color }}>
                        {rarityConfig.label}
                      </span>
                    </div>
                  )}

                  {/* Registration Plate */}
                  <div
                    className="absolute bottom-2 left-2 px-2 py-1 rounded text-xs font-mono"
                    style={{ backgroundColor: 'rgba(0,0,0,0.8)', color: '#a3a3a3' }}
                  >
                    {ac.registration || ac.id?.slice(0, 10)}
                  </div>
                </div>

                <CardContent className="p-4">
                  {/* Aircraft Name */}
                  <h3 className="text-xl font-bold mb-1" style={{ color: '#e5e5e5' }}>
                    {ac.name || specs.model || ac.model}
                  </h3>

                  {/* Manufacturer */}
                  <div className="text-sm mb-3" style={{ color: '#737373' }}>
                    {specs.manufacturer || t('museum.unknownManufacturer')}
                  </div>

                  {/* Lore/Description */}
                  <p className="text-sm mb-4 line-clamp-2" style={{ color: '#a3a3a3', minHeight: '2.5rem' }}>
                    {lore}
                  </p>

                  {/* Quick Stats */}
                  <div className="flex items-center justify-between text-xs pt-3 border-t" style={{ borderColor: '#333' }}>
                    <div className="flex items-center gap-1" style={{ color: '#737373' }}>
                      <Users className="w-3 h-3" />
                      <span>{specs.capacity || '?'}</span>
                    </div>
                    <div className="flex items-center gap-1" style={{ color: '#737373' }}>
                      <Gauge className="w-3 h-3" />
                      <span>{specs.range || '?'} {t('museum.nm')}</span>
                    </div>
                    <div className="flex items-center gap-1" style={{ color: '#737373' }}>
                      <Clock className="w-3 h-3" />
                      <span>{Math.floor(ac.totalFlightHours || 0)} {t('museum.hrs')}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
